import type { Dispatch, SetStateAction } from 'react';

import { File, FileImage, Trash2, X } from 'lucide-react';
import { Button } from '../../ui/button';
import {
	formatAttachmentSize,
	isImageAttachment,
	releaseAttachmentPreview,
	type QueuedAttachment,
} from '../attachment';

interface FileAttachmentProps {
	files: QueuedAttachment[];
	setFiles: Dispatch<SetStateAction<QueuedAttachment[]>>;
}
export function ChatRoomAttachmentFile(fileAttachmentProps: FileAttachmentProps) {
	const { files, setFiles } = fileAttachmentProps;
	const removeFile = (id: string) => {
		setFiles((currentFiles) => {
			const removedFile = currentFiles.find((file) => file.id === id);
			if (removedFile) {
				releaseAttachmentPreview(removedFile);
			}
			return currentFiles.filter((file) => file.id !== id);
		});
	};

	const clearFiles = () => {
		setFiles((currentFiles) => {
			currentFiles.forEach(releaseAttachmentPreview);
			return [];
		});
	};

	return (
		<div className="absolute inset-x-0 bottom-0 z-10 max-h-[60%] overflow-y-auto rounded-t-2xl border border-b-0 border-slate-200 bg-white/95 p-3 shadow-[0_-12px_30px_rgba(15,23,42,0.12)] backdrop-blur sm:p-4">
			<div className="mb-3 flex items-center justify-between gap-3">
				<div>
					<div className="text-sm font-semibold text-slate-900">Ready to send</div>
					<div className="text-xs text-slate-500">{files.length} of 4 attachments</div>
				</div>
				<Button aria-label="Remove all attachments" className="text-slate-500 hover:text-destructive" onClick={clearFiles} size="icon-sm" type="button" variant="ghost">
					<X className="size-4" />
				</Button>
			</div>
			<div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
				{files.map((attachment) => (
					<div className="group relative min-w-0 overflow-hidden rounded-xl border border-slate-200 bg-slate-50" key={attachment.id}>
						{isImageAttachment(attachment) && attachment.previewUrl ? (
							<img alt={attachment.file.name} className="aspect-square w-full object-cover" src={attachment.previewUrl} />
						) : (
							<div className="flex aspect-square items-center justify-center bg-primary/5 text-primary">
								{attachment.file.type.startsWith('image/') ? <FileImage className="size-9" /> : <File className="size-9" />}
							</div>
						)}
						<div className="min-w-0 border-t border-slate-200 bg-white px-2 py-1.5">
							<div className="truncate text-xs font-medium text-slate-800">{attachment.file.name}</div>
							<div className="text-[11px] text-slate-500">{formatAttachmentSize(attachment.file.size)}</div>
						</div>
						<Button
							aria-label={`Remove ${attachment.file.name}`}
							className="absolute right-1.5 top-1.5 bg-white/90 text-slate-700 shadow-sm hover:bg-white hover:text-destructive"
							onClick={() => removeFile(attachment.id)}
							size="icon-xs"
							type="button"
							variant="ghost"
						>
							<Trash2 className="size-3.5" />
						</Button>
					</div>
				))}
			</div>
		</div>
	);
}
